import {
  GHOST_POWER_BP,
  BIG_PARTY,
  INCURSION,
  INCURSION_DANGER,
  INCURSION_ITEM_BP,
  INCURSION_OMEN_LIST,
  INCURSION_OMENS,
  OMENS_BEFORE,
  OMENS_WIDE_FROM,
  INCURSION_RINGS,
  INCURSION_SITES,
  INCURSION_SPAWN_SPACING,
  INCURSION_TIERS,
  MAX_RELICS,
  RELIC_DROPS,
  INCURSION_TWISTS,
  INCURSION_MISSIONS,
  MISSION_WEIGHTS,
  MISSIONS_FROM_GROUP,
  PLUNDER,
  MAREAS,
  mareaInkBp,
  NIGHTMARES,
  RELICS,
  ALL_RELICS,
  RELIC_SPARE_EMBERS,
  XP,
  res,
  type IncursionMission,
  type IncursionOmen,
  type OmenDef,
  type IncursionRisk,
  type NightmareType,
  type NodeKind,
  type RelicId,
  IRON_MINE,
  SAPPER_FORT,
  TOMES,
  GLYPH_FRAGS,
  LOOT_POWER,
  easeShare,
} from '@somnhold/content';
import type { BattleParams } from '../battle/create.ts';
import { createIncursionMap } from '../map.ts';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { deriveSeed, rngChance, rngInt, rngWeighted, seedRng } from '../math/rng.ts';
import { snapshot } from '../math/hash.ts';
import { hexLen, hexRing, type Hex } from '../hex.ts';
import { plunderShareBp } from '../battle/plunder.ts';
import type { BattleRules, BattleState, MapDef, Spawn } from '../types.ts';
import { addRes, event, findPlayer, heroName } from './core.ts';
import { eraOf, groupReached, withDelves } from './eras.ts';
import { giveItem, lootCtx, rollItem, lookOf } from './items.ts';
import { afflictAfter, carried, gearMods, grantXp, perkBp, addGlyphFragment, addPages } from './progress.ts';
import type { CampaignState, Ms, PlayerState } from './types.ts';
import { creditGathered, tallyDeeds } from './deeds.ts';
import { tallyFeats } from './feats.ts';
import { logOuting } from './outings.ts';

export interface IncursionNode {
  id: number;
  kind: NodeKind;
  /** 1 near the bastion (the shell), 2 the flanks and legs, 3 the head, feet and tail. */
  ring: 1 | 2 | 3;
  /** Position on the titan body map, 0..1000 on both axes. */
  x: number;
  y: number;
  /** Double reward (revealed by rangers). */
  double: boolean;
  /** Open but not yet reachable (ring 3 below its group level). */
  locked: boolean;
}

/** Places the group has opened (ring 1 from the start). */
export const openSites = (s: CampaignState): number[] => s.explored?.open ?? INCURSION_SITES.filter((x) => x.ring === 1).map((x) => x.id);

/**
 * Today's places (SPEC §13): every place the group has opened, with its own kind of land. Each day,
 * from the campaign seed, one of them hides a lost golem and one gives double.
 */
export const dayNodes = (s: CampaignState, day: number): IncursionNode[] => {
  const rng = seedRng(deriveSeed(s.seed, 5000 + day));
  const open = new Set(openSites(s));
  // The iron mines open by themselves with era II.
  const era = eraOf(s);
  for (const x of INCURSION_SITES) if (x.minEra !== undefined && era >= x.minEra) open.add(x.id);
  const nodes: IncursionNode[] = INCURSION_SITES.filter((x) => open.has(x.id)).map((x) => ({
    id: x.id,
    kind: x.kind,
    ring: x.ring,
    x: x.x,
    y: x.y,
    double: false,
    locked: !groupReached(s, INCURSION_RINGS[x.ring].minGroup),
  }));
  // (Mines never hide the lost golem nor give double: the day's draw stays what it was before them.)
  const free = nodes.filter((n) => !n.locked && n.kind !== 'lair' && n.kind !== 'iron' && n.kind !== 'fort');
  if (free.length) (free[rngInt(rng, free.length)] as IncursionNode).kind = 'rescue';
  // One doubled place a day: with only a handful open early on, two would be most of the map.
  if (free.length) (free[rngInt(rng, free.length)] as IncursionNode).double = true;
  return nodes;
};

/** A won incursion opens the neighboring places for everyone. Returns the newly opened ones. */
export const exploreFrom = (s: CampaignState, pid: string, siteId: number, now: Ms): number[] => {
  const site = INCURSION_SITES.find((x) => x.id === siteId);
  if (!site) return [];
  const open = openSites(s).slice();
  const opened = site.next.filter((n) => !open.includes(n));
  if (!opened.length) return [];
  open.push(...opened);
  open.sort((a, b) => a - b);
  const by = { ...(s.explored?.by ?? {}) };
  for (const n of opened) by[String(n)] = pid;
  s.explored = { ...s.explored, open, by };
  event(s, now, 'explore', { pid, data: { from: siteId, opened: opened.join(',') } });
  return opened;
};

/** An incursion level: 1 the plain one, 2 Ascuas (from group level 5), 3 Obsidiana (from 8). */
export type IncursionTier = 1 | 2 | 3;

/** Whether a place can be played at a level: level II from group level 5, once the group won that place at level I. */
export const tierOpen = (s: CampaignState, siteId: number, tier: IncursionTier): boolean => {
  if (tier === 1) return true;
  if (tier === 2) return groupReached(s, INCURSION_TIERS[2].minGroup) && (s.explored?.won ?? []).includes(siteId);
  return groupReached(s, INCURSION_TIERS[3].minGroup) && (s.explored?.won2 ?? []).includes(siteId);
};

/** Notes a place as won at a level (it opens the next level there). */
const markWon = (s: CampaignState, siteId: number, tier: IncursionTier): void => {
  const ex = s.explored ?? { open: openSites(s), by: {} };
  const key = tier === 1 ? 'won' : tier === 2 ? 'won2' : 'won3';
  const list = ex[key] ?? [];
  if (!list.includes(siteId)) ex[key] = [...list, siteId].sort((a, b) => a - b);
  s.explored = ex;
};

const TURRET_LIMIT = 3;

/** The day's omen, the same for the whole group and every node of the day. */
export const dayOmen = (s: CampaignState, day: number): IncursionOmen => {
  const rng = seedRng(deriveSeed(s.seed, 7000 + day));
  // Days that began before the newer omens existed keep drawing among the first ones.
  const n = s.createdAt + day * 86_400_000 >= OMENS_WIDE_FROM ? INCURSION_OMEN_LIST.length : OMENS_BEFORE;
  return INCURSION_OMEN_LIST[rngInt(rng, n)] as IncursionOmen;
};

/** The newer omens' rules (harder blows, lifesteal, lightning, falling stars), as a battle carries them. */
export const omenRules = (rules: BattleRules, omen: OmenDef): BattleRules => {
  if (omen.enemyDamageBp) rules.enemyDamageBp = omen.enemyDamageBp;
  if (omen.heroLifestealBp) rules.heroLifestealBp = omen.heroLifestealBp;
  if (omen.bolts) rules.bolts = { ...omen.bolts };
  if (omen.stars) rules.stars = { ...omen.stars };
  return rules;
};

/**
 * The seed of a place's next run: the day's, mixed with how many incursions the group has played, so
 * running the same place again lays out a new field. The first run of all keeps the plain day seed. `runs`
 * pins the count (a live room keeps the one it opened with).
 */
export const incursionSeed = (s: CampaignState, day: number, nodeId: number, runs = s.incursionRuns ?? 0): number => {
  const base = deriveSeed(s.seed, 9000 + day * 32 + nodeId);
  return runs ? deriveSeed(base, runs) : base;
};

/** What the next run at a place asks (shown on its card before going in): drawn per run, by the lay of the land. */
export const incursionMission = (s: CampaignState, nodeId: number, day: number, runs = s.incursionRuns ?? 0): IncursionMission => {
  const node = dayNodes(s, day).find((n) => n.id === nodeId);
  const w = node ? MISSION_WEIGHTS[node.kind] : undefined;
  if (!w || !groupReached(s, MISSIONS_FROM_GROUP)) return 'extract';
  const entries = INCURSION_MISSIONS.filter((m) => (w[m] ?? 0) > 0).map((m) => [m, w[m] as number] as const);
  if (entries.length < 2) return 'extract';
  return rngWeighted(seedRng(incursionSeed(s, day, nodeId, runs) ^ 0x3155), entries);
};

/** Companions as given (one id from older callers, or a list), without the leader, duplicates or the live ones. */
const ghostsOf = (pid: string, companions: string | string[] | null, live: string[]): string[] =>
  [...new Set(companions === null ? [] : Array.isArray(companions) ? companions : [companions])].filter((c) => c !== pid && !live.includes(c));

/**
 * Battle for an incursion: the player's hero defends a node while it is extracted, with up to seven
 * companions — friends' ghosts or friends playing live. Every companion brings more nightmares. `runs` pins
 * the place's run (incursionSeed): a live room rebuilt in its lobby after someone else's run lays out the same field.
 */
export const incursionParams = (s: CampaignState, pid: string, nodeId: number, day: number, companions: string | string[] | null, risk: IncursionRisk = 'normal', live: string[] = [], tier: IncursionTier = 1, runs = s.incursionRuns ?? 0): BattleParams | null => {
  const p = findPlayer(s, pid);
  const node = dayNodes(s, day).find((n) => n.id === nodeId);
  if (!p || !node || node.locked || !tierOpen(s, nodeId, tier)) return null;
  // Level II and III share their shape: tougher, a boss at the end.
  const ascuas = tier === 2 ? INCURSION_TIERS[2] : tier === 3 ? INCURSION_TIERS[3] : null;
  const ring = INCURSION_RINGS[node.ring];
  const seed = incursionSeed(s, day, nodeId, runs);
  const mission = incursionMission(s, nodeId, day, runs);
  const map = createIncursionMap(seed);
  const rng = seedRng(seed ^ 0x1ac);
  const omenId = dayOmen(s, day);
  const omen = INCURSION_OMENS[omenId];
  const danger = risk === 'danger';
  let budget = mulBp(INCURSION.budgetBase, 10000 + p.hero.level * INCURSION.budgetLevelBp);
  if (node.kind === 'crystal') budget = mulBp(budget, INCURSION_TWISTS.crystal.budgetBp);
  if (node.kind === 'rescue') budget = mulBp(budget, INCURSION_TWISTS.rescue.budgetBp);
  if (omen.budgetBp) budget = mulBp(budget, omen.budgetBp);
  if (danger) budget = mulBp(budget, INCURSION_DANGER.budgetBp);
  budget = mulBp(budget, ring.budgetBp);
  if (ascuas) budget = mulBp(budget, ascuas.budgetBp);
  const liveIn = [...new Set(live)].filter((x) => x !== pid && !!findPlayer(s, x)).slice(0, INCURSION.maxCompanions);
  const ghosts = ghostsOf(pid, companions, liveIn).filter((x) => !!findPlayer(s, x)).slice(0, INCURSION.maxCompanions - liveIn.length);
  // Up to three companions (live first) as measured; each one past that brings a share of a party of four, part as
  // more nightmares and the rest as tougher ones (BIG_PARTY).
  const liveM = imin(liveIn.length, INCURSION.measuredCompanions);
  const ghostM = imin(ghosts.length, INCURSION.measuredCompanions - liveM);
  const beyond = (liveIn.length - liveM) * 10000 + (ghosts.length - ghostM) * idiv(INCURSION.ghostBudgetBp * 10000, INCURSION.liveBudgetBp);
  const k = idiv(beyond, 10000);
  const big = 10000 + idiv(mulBp(beyond, INCURSION.big.extraBp) + idiv(k * (k - 1), 2) * INCURSION.big.rampBp, BIG_PARTY.measured);
  const bigCount = 10000 + mulBp(big - 10000, BIG_PARTY.countBp);
  budget = mulBp(budget, 10000 + ghostM * INCURSION.ghostBudgetBp + liveM * INCURSION.liveBudgetBp);
  if (big !== 10000) budget = mulBp(budget, bigCount);
  const bigHp = big !== 10000 ? idiv(big * 10000, bigCount) : 10000;
  const def = MAREAS[imin(s.mareaIndex, MAREAS.length - 1)] as (typeof MAREAS)[number];
  // The sappers' fort is held by sappers (and a few archers).
  // The era's creatures come out here too.
  const table: Partial<Record<NightmareType, number>> = withDelves(s, def.weights);
  if (node.kind === 'fort') for (const k of Object.keys(SAPPER_FORT.weights).sort() as NightmareType[]) table[k] = (table[k] ?? 0) + (SAPPER_FORT.weights[k] ?? 0);
  const entries = (Object.keys(table) as NightmareType[]).sort().map((k) => [k, table[k] as number] as const);
  // The toughest nightmare of the Marea leads as an elite (altar, dangerous runs).
  const eliteKind = entries.reduce((a, b) => (NIGHTMARES[b[0]].points > NIGHTMARES[a[0]].points ? b : a))[0];
  const inkBp = imin(10000, (s.depth === 'deep' ? mareaInkBp(s.mareaIndex) : 0) + (omen.inkBp ?? 0) + ring.inkBp);
  const spacing = node.kind === 'rescue' ? INCURSION_TWISTS.rescue.spacing : INCURSION_SPAWN_SPACING;
  // Forest ambush spots: the edge of the clearing, off the paths.
  const onPath = new Set(map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
  const woods = node.kind === 'forest' ? hexRing(map.bastion).filter((h) => !onPath.has(`${h.q},${h.r}`)) : [];
  const spawns: Spawn[] = [];
  const third = idiv(INCURSION.extractTicks, INCURSION.waves);
  const plunder = mission === 'plunder';
  if (plunder) spawns.push(...plunderSpawns(rng, budget, entries, eliteKind, inkBp, (danger ? INCURSION_DANGER.elitePerWave : 0) + ring.elitePerWave, map.sectors.length, spacing));
  else for (let w = 0; w < INCURSION.waves; w++) {
    const start = spawns.length;
    let left = budget;
    let i = 0;
    while (left > 0 && i < 90) {
      const affordable = entries.filter(([k]) => NIGHTMARES[k].points <= left);
      if (affordable.length === 0) break;
      const kind = rngWeighted(rng, affordable);
      left -= NIGHTMARES[kind].points;
      const tier: 1 | 2 = inkBp > 0 && rngChance(rng, inkBp) ? 2 : 1;
      // `tick` holds the slot within the wave until the wave is spread over its third, below.
      const sp: Spawn = { tick: i, sector: rngInt(rng, map.sectors.length), kind, tier, elite: false };
      if (woods.length && rngChance(rng, INCURSION_TWISTS.forest.ambushBp)) sp.at = woods[rngInt(rng, woods.length)] as { q: number; r: number };
      spawns.push(sp);
      i++;
    }
    const last = w === INCURSION.waves - 1;
    const elites =
      (danger ? INCURSION_DANGER.elitePerWave : 0) +
      ring.elitePerWave +
      (last ? ring.finalElites + (node.kind === 'altar' ? 1 : 0) + (node.kind === 'lair' ? INCURSION_TWISTS.lair.finalElites : 0) : 0);
    for (let k = 0; k < elites; k++) spawns.push({ tick: i + 2 + k * 4, sector: rngInt(rng, map.sectors.length), kind: eliteKind, tier: 1, elite: true });
    // The whole wave, elites included, comes within its third of the extraction: a big one comes faster.
    const slots = elites ? i + 3 + (elites - 1) * 4 : i;
    if (node.kind === 'rescue') {
      // The golem's cries: tight packs, a breather between them, all within the wave's third.
      const { packs, packGap } = INCURSION_TWISTS.rescue;
      const per = imax(1, idiv(slots + packs - 1, packs));
      for (let k = start; k < spawns.length; k++) {
        const sp = spawns[k] as Spawn;
        const pack = imin(packs - 1, idiv(sp.tick, per));
        sp.tick = 60 + w * third + pack * (packGap + per * spacing) + (sp.tick - pack * per) * spacing;
      }
      continue;
    }
    const step = imin(spacing, idiv(third - 100, imax(1, slots)));
    for (let k = start; k < spawns.length; k++) {
      const sp = spawns[k] as Spawn;
      sp.tick = 60 + w * third + sp.tick * step;
    }
  }
  spawns.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
  // Ascuas: the magma lord comes up behind the last of them; Obsidiana: the colossus.
  // (On a plunder, with the third wave.)
  if (ascuas) {
    const at = plunder ? PLUNDER.firstWave + 2 * PLUNDER.waveEvery : (spawns.length ? (spawns[spawns.length - 1] as Spawn).tick : 60) + 24;
    spawns.push({ tick: at, sector: rngInt(rng, map.sectors.length), kind: tier === 3 ? 'colossus' : 'magmaLord', tier: 1, elite: false });
    if (plunder) spawns.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
  }
  const heroes: BattleParams['heroes'] = [
    { pid, name: heroName(p), cls: p.hero.heroClass, level: p.hero.level, control: 'live', powerBp: 10000, post: 0, relics: p.hero.relics, awakened: p.hero.awakened ?? [], gear: gearMods(p.hero), ...lookOf(p.hero), ...carried(p.hero, 'incursion', s) },
  ];
  for (const id of liveIn) {
    const f = findPlayer(s, id) as NonNullable<ReturnType<typeof findPlayer>>;
    heroes.push({ pid: f.id, name: heroName(f), cls: f.hero.heroClass, level: f.hero.level, control: 'live', powerBp: 10000, post: 0, relics: f.hero.relics, awakened: f.hero.awakened ?? [], gear: gearMods(f.hero), ...lookOf(f.hero), ...carried(f.hero, 'incursion', s) });
  }
  ghosts.forEach((id, i) => {
    const c = findPlayer(s, id) as NonNullable<ReturnType<typeof findPlayer>>;
    heroes.push({ pid: c.id, name: heroName(c), cls: c.hero.heroClass, level: c.hero.level, control: 'ghost', powerBp: GHOST_POWER_BP, post: 1 + (i % 2), profile: c.ghostProfile, relics: c.hero.relics, awakened: c.hero.awakened ?? [], gear: gearMods(c.hero), ...lookOf(c.hero), ...carried(c.hero, 'incursion', s) });
  });
  const rules: BattleRules = { twist: node.kind, omen: omenId, risk, turrets: { ...INCURSION.turrets }, clearToFinish: true, eliteHpBp: INCURSION.eliteHpBp };
  if (plunder) {
    rules.mission = 'plunder';
    delete rules.clearToFinish;
  }
  // (A plunder's cart takes blows; the heroes still have to be able to hold them off it.)
  if (node.kind === 'crystal' && mission !== 'plunder') rules.rush = true;
  if (ascuas) rules.tier = tier === 3 ? 3 : 2;
  if (omen.enemySpeedBp) rules.enemySpeedBp = omen.enemySpeedBp;
  rules.enemyHpBp = mulBp(mulBp(omen.enemyHpBp ?? 10000, INCURSION.hpBp), 10000 + ghostM * INCURSION.ghostHpBp + liveM * INCURSION.liveHpBp);
  if (big !== 10000) rules.enemyHpBp = mulBp(rules.enemyHpBp, bigHp);
  // Level III eases from the level the unique armour used to come at (docs/UNICAS.md §5).
  if (ascuas) rules.enemyHpBp = mulBp(rules.enemyHpBp, tier === 3 ? ascuas.hpBp + idiv((INCURSION_TIERS[3].easedHpBp - ascuas.hpBp) * easeShare((p.hero.level - 1) * 100), 10000) : ascuas.hpBp);
  if (plunder) rules.enemyHpBp = mulBp(rules.enemyHpBp, PLUNDER.hpBp);
  if (omen.heroRangeBp) rules.heroRangeBp = omen.heroRangeBp;
  omenRules(rules, omen);
  // Shares nothing with the campaign: the server re-simulates from it after the state moved on.
  return snapshot({
    mode: 'incursion',
    seed,
    map,
    structures: [],
    heroes,
    // Past three companions the nightmares are tougher and stand at the node longer: it holds half as much more.
    keepHp: mulBp(1500, 10000 + idiv(bigHp - 10000, 2)),
    keepMaxHp: mulBp(1500, 10000 + idiv(bigHp - 10000, 2)),
    marea: s.mareaIndex,
    wave: 0,
    spawns,
    ...(plunder
      ? { objectiveTicks: PLUNDER.maxTicks, plunder: { caches: plunderCaches(seed, map, heroes.filter((h) => h.control === 'live').length) } }
      : { objectiveTicks: INCURSION.extractTicks, objectiveQuietRate: INCURSION.quietRate }),
    rules,
  });
};

/**
 * A plunder's waves (docs/MISIONES.md): one every `waveEvery`, each bigger than the last, with draggers at
 * their head; the run lasts until the group leaves, at most `maxTicks`.
 */
export const plunderSpawns = (rng: ReturnType<typeof seedRng>, budget: number, entries: (readonly [NightmareType, number])[], eliteKind: NightmareType, inkBp: number, elites: number, sectors: number, spacing: number): Spawn[] => {
  const P = PLUNDER;
  const out: Spawn[] = [];
  for (let w = 0; P.firstWave + w * P.waveEvery < P.maxTicks; w++) {
    const wave: Spawn[] = [];
    const draggers = P.draggers.base + idiv(w, P.draggers.per);
    for (let k = 0; k < draggers; k++) wave.push({ tick: 0, sector: rngInt(rng, sectors), kind: 'dragger', tier: 1, elite: false });
    let left = mulBp(mulBp(budget, P.waveBudgetBp), 10000 + w * P.waveGrowBp);
    let i = 0;
    while (left > 0 && i < 90) {
      const affordable = entries.filter(([k]) => NIGHTMARES[k].points <= left);
      if (affordable.length === 0) break;
      const kind = rngWeighted(rng, affordable);
      left -= NIGHTMARES[kind].points;
      const tier: 1 | 2 = inkBp > 0 && rngChance(rng, inkBp) ? 2 : 1;
      wave.push({ tick: 0, sector: rngInt(rng, sectors), kind, tier, elite: false });
      i++;
    }
    for (let k = 0; k < elites; k++) wave.push({ tick: 0, sector: rngInt(rng, sectors), kind: eliteKind, tier: 1, elite: true });
    // The whole wave comes within the first half of its time.
    const step = imin(spacing, idiv(idiv(P.waveEvery, 2), imax(1, wave.length)));
    wave.forEach((sp, k) => {
      sp.tick = P.firstWave + w * P.waveEvery + k * step;
    });
    out.push(...wave);
  }
  return out.sort((a, b) => a.tick - b.tick || a.sector - b.sector);
};

/**
 * Where a plunder's caches lie: on open ground, the clearing's outer rings and the roads out of it (never the
 * holes), apart from each other; the coffers (with two or more heroes playing live) far out on the roads.
 */
export const plunderCaches = (seed: number, map: MapDef, live: number): { kind: 'chest' | 'coffer'; q: number; r: number }[] => {
  const P = PLUNDER;
  const rng = seedRng(seed ^ 0xca5e);
  const near: Hex[] = [];
  for (let n = P.minRing; n <= map.bastion; n++) near.push(...hexRing(n));
  const road: Hex[] = [];
  for (const sec of map.sectors) for (const h of sec.path.slice(1)) if (hexLen(h.q, h.r) > map.bastion) road.push(h);
  // Each shuffled once from the seed, then taken in order, never next to one already taken (on a crowded road, never on one).
  const shuffle = (xs: Hex[]): Hex[] => {
    for (let i = xs.length - 1; i > 0; i--) {
      const j = rngInt(rng, i + 1);
      const t = xs[i] as Hex;
      xs[i] = xs[j] as Hex;
      xs[j] = t;
    }
    return xs;
  };
  shuffle(near);
  // Out on the roads, the far ends first: the long carries are the run.
  shuffle(road).sort((a, b) => hexLen(b.q, b.r) - hexLen(a.q, a.r));
  const out: { kind: 'chest' | 'coffer'; q: number; r: number }[] = [];
  const take = (pool: Hex[], kind: 'chest' | 'coffer', n: number, minRing: number): void => {
    let got = 0;
    for (const gap of [2, 1]) {
      for (const h of pool) {
        if (got >= n) return;
        if (hexLen(h.q, h.r) >= minRing && out.every((o) => hexLen(o.q - h.q, o.r - h.r) >= gap)) {
          out.push({ kind, q: h.q, r: h.r });
          got++;
        }
      }
    }
  };
  if (live >= 2) take(road, 'coffer', P.coffers, map.bastion + P.cofferOut);
  take(near, 'chest', P.nearChests, P.minRing);
  take(road, 'chest', P.chests - P.nearChests, 0);
  return out;
};

export const TURRETS_PER_INCURSION = TURRET_LIMIT;

export interface IncursionResult {
  ok: boolean;
  reason?: string;
  full?: boolean;
  share?: number;
}

/** Applies the outcome of a re-simulated incursion: resources to the storage, dust, XP, loot (SPEC §13). */
export const applyIncursion = (s: CampaignState, pid: string, nodeId: number, day: number, companions: string | string[] | null, battle: BattleState, now: Ms, risk: IncursionRisk = 'normal', live: string[] = [], tier: IncursionTier = 1): IncursionResult => {
  const ascuas = tier === 2 ? INCURSION_TIERS[2] : tier === 3 ? INCURSION_TIERS[3] : null;
  const p = findPlayer(s, pid);
  const node = dayNodes(s, day).find((n) => n.id === nodeId);
  if (!p || !node) return { ok: false, reason: 'noNode' };
  tallyDeeds(s, 'incursion', battle, now);
  if (battle.result === 'running') return { ok: false, reason: 'unfinished' };
  // (An unfinished one is turned down: its feats aren't counted either.)
  tallyFeats(s, battle, {}, now);
  s.incursionRuns = (s.incursionRuns ?? 0) + 1;
  const obj = battle.objective;
  // A plunder pays for what made it to the cart (half if they fell), up to `PLUNDER.capBp` of an extraction.
  const plunder = battle.rules?.mission === 'plunder' ? battle.plunder : undefined;
  const progressBp = plunder ? plunderShareBp(plunder) : obj ? (battle.result === 'won' ? 10000 : idiv(obj.progress * 10000, obj.total)) : 0;
  // Everyone who came live spends one of their runs of the day and brings their own share (full or reduced, as
  // their day goes), so going together never pays less than going apart.
  const liveIn = [...new Set(live)].filter((x) => x !== pid && !!findPlayer(s, x));
  const runShare = (q: PlayerState): number => {
    if (q.incursionDay !== day) {
      q.incursionDay = day;
      q.incursionsToday = 0;
    }
    q.incursionsToday++;
    return q.incursionsToday <= INCURSION.fullPerDay ? 10000 : INCURSION.reducedBp;
  };
  const full = runShare(p) === 10000;
  let crewBp = full ? 10000 : INCURSION.reducedBp;
  for (const id of liveIn) crewBp += mulBp(runShare(findPlayer(s, id) as PlayerState), INCURSION.liveRewardBp);
  let shareBp = mulBp(progressBp, crewBp);
  if (node.double) shareBp *= 2;
  shareBp = mulBp(shareBp, 10000 + perkBp(p.hero, 'incursionLoot'));
  const ring = INCURSION_RINGS[node.ring];
  const riskBp = mulBp(mulBp(risk === 'danger' ? INCURSION_DANGER.rewardBp : 10000, ring.rewardBp), ascuas ? ascuas.rewardBp : 10000);
  shareBp = mulBp(shareBp, riskBp);
  const amount = mulBp(INCURSION.reward, shareBp);
  const dust = mulBp(INCURSION.rewardDust, shareBp);
  const loot = res({ dust });
  if (node.kind === 'quarry') loot.stone += amount;
  else if (node.kind === 'forest') loot.wood += amount;
  else if (node.kind === 'crystal') loot.crystal += amount;
  else if (node.kind === 'altar' || node.kind === 'lair') loot.dust += amount;
  else if (node.kind === 'iron') loot.iron += mulBp(amount, IRON_MINE.rewardBp);
  else if (node.kind === 'fort') {
    // The fort gives dust, and a win keeps sappers out of the next wave.
    loot.dust += amount;
    if (battle.result === 'won') s.sappersCleared = { marea: s.mareaIndex, wave: s.waveIndex };
  }
  else {
    // Rescue: a lost golem comes home and works for the rest of the Marea.
    loot.wood += idiv(amount, 2);
    loot.stone += idiv(amount, 2);
    if (battle.result === 'won') s.extraGolems = { marea: s.mareaIndex, count: (s.extraGolems?.marea === s.mareaIndex ? s.extraGolems.count : 0) + 1 };
  }
  addRes(s.storage, loot);
  creditGathered(s, [pid, ...liveIn], loot);
  const xp = mulBp(mulBp(XP.incursion, progressBp), riskBp);
  grantXp(s, pid, xp, now);
  // A win: a page of the medium slot's tome for each hero who fought it live.
  if (battle.result === 'won')
    for (const id of [pid, ...liveIn]) {
      addPages(s, id, 1, TOMES.incursion, now);
      // And a glyph fragment (docs/GLIFOS.md §3).
      addGlyphFragment(s, id, 'incursion', GLYPH_FRAGS.incursion, now);
    }
  // Live friends earn as the leader does; each ghost's owner gets 10 % of the XP, and the log says so.
  for (const id of liveIn) grantXp(s, id, xp, now);
  for (const id of ghostsOf(pid, companions, liveIn)) {
    const c = findPlayer(s, id);
    if (!c) continue;
    const share = mulBp(xp, INCURSION.carryShareBp);
    grantXp(s, c.id, share, now);
    if (share > 0) event(s, now, 'carried', { pid: c.id, data: { by: pid, xp: share } });
  }
  if (ascuas) {
    // Embers (level II) or obsidian shards (level III) for the forge: the whole share on a win (live friends too), a part of it for a run cut short.
    const n = 'embers' in ascuas ? ascuas.embers : ascuas.shards;
    const give = (q: PlayerState, k: number): void => {
      if (k <= 0) return;
      if (tier === 3) q.hero.shards = (q.hero.shards ?? 0) + k;
      else q.hero.embers = (q.hero.embers ?? 0) + k;
    };
    if (battle.result === 'won') {
      give(p, n);
      for (const id of liveIn) {
        const f = findPlayer(s, id);
        if (f) give(f, n);
      }
      // The boss's piece: uncommon at least, often rare.
      giveItem(s, p, rollItem(s, s.rng, rngChance(s.rng, ascuas.bossRareBp) ? 'rare' : 'uncommon', lootCtx(s, p, LOOT_POWER.incursion[tier] ?? 0, 'incursion', day)), now);
    } else give(p, mulBp(n, progressBp));
  }
  if (battle.result === 'won') {
    markWon(s, nodeId, tier);
    for (const id of liveIn) {
      const f = findPlayer(s, id);
      if (f && rngChance(s.rng, imax(ring.itemBp, INCURSION_ITEM_BP))) giveItem(s, f, rollItem(s, s.rng, undefined, lootCtx(s, f, LOOT_POWER.incursion[tier] ?? 0, 'incursion', day)), now);
    }
    // A warlord's den always leaves a rare piece; elsewhere the farther, the likelier.
    if (node.kind === 'lair') giveItem(s, p, rollItem(s, s.rng, 'rare', lootCtx(s, p, (LOOT_POWER.incursion[tier] ?? 0) + LOOT_POWER.lair, 'lair', day)), now);
    else if (rngChance(s.rng, imax(ring.itemBp, risk === 'danger' ? INCURSION_DANGER.itemBp : INCURSION_ITEM_BP))) giveItem(s, p, rollItem(s, s.rng, undefined, lootCtx(s, p, LOOT_POWER.incursion[tier] ?? 0, 'incursion', day)), now);
    const relic = node.kind === 'altar' ? 'vigilGrimoire' : node.kind === 'rescue' ? 'warHorn' : null;
    if (relic && rngChance(s.rng, node.ring === 3 ? 3000 : 1500)) grantRelic(s, pid, relic, now);
    // The warlord of a den: a chance at a relic for everyone who fought it live.
    if (node.kind === 'lair') for (const id of [pid, ...liveIn]) rollRelic(s, id, RELIC_DROPS.lair, now);
    // The magma lord and the colossus, likewise.
    if (ascuas) for (const id of [pid, ...liveIn]) rollRelic(s, id, tier === 3 ? RELIC_DROPS.colossus : RELIC_DROPS.magmaLord, now);
    exploreFrom(s, pid, nodeId, now);
  }
  afflictAfter(s, battle, now);
  logOuting(s, now, `inc${tier}`, [pid, ...liveIn], battle.result === 'won');
  event(s, now, 'incursion', { pid, data: { node: nodeId, kind: node.kind, ring: node.ring, ...(liveIn.length ? { live: liveIn.join(',') } : {}), ...(risk === 'danger' ? { danger: true } : {}), ...(ascuas ? { tier } : {}), ...(plunder ? { mission: 'plunder', loaded: plunder.loaded } : {}), won: battle.result === 'won', wood: loot.wood, stone: loot.stone, crystal: loot.crystal, dust: loot.dust, ...(loot.iron ? { iron: loot.iron } : {}) } });
  s.version++;
  return { ok: true, full, share: shareBp };
};

/**
 * Gives a relic: carried if a slot is free, else kept in reserve for the player to swap in (never lost).
 * Nothing if the hero already has it either way. The log tells which.
 */
export const grantRelic = (s: CampaignState, pid: string, relic: RelicId, now: Ms): 'carried' | 'stored' | false => {
  const p = findPlayer(s, pid);
  if (!p || !ALL_RELICS.includes(relic) || p.hero.relics.includes(relic) || (p.hero.relicBag ?? []).includes(relic)) return false;
  if (p.hero.relics.length < MAX_RELICS) {
    p.hero.relics.push(relic);
    event(s, now, 'loot', { pid, data: { relic } });
    return 'carried';
  }
  p.hero.relicBag = [...(p.hero.relicBag ?? []), relic];
  event(s, now, 'loot', { pid, data: { relic, stored: true } });
  return 'stored';
};

/** The relics a hero has neither carried nor kept. */
export const missingRelics = (p: PlayerState): RelicId[] => RELICS.filter((r) => !p.hero.relics.includes(r) && !(p.hero.relicBag ?? []).includes(r));

/** A chance (bp) at a relic the hero doesn't have yet; with all of them, it melts into embers. */
export const rollRelic = (s: CampaignState, pid: string, bp: number, now: Ms): void => {
  const p = findPlayer(s, pid);
  if (!p || !rngChance(s.rng, bp)) return;
  const missing = missingRelics(p);
  if (missing.length) grantRelic(s, pid, missing[rngInt(s.rng, missing.length)] as RelicId, now);
  else {
    p.hero.embers = (p.hero.embers ?? 0) + RELIC_SPARE_EMBERS;
    event(s, now, 'loot', { pid, data: { relicEmbers: RELIC_SPARE_EMBERS } });
  }
};

/** Campaign day index of an instant: days since creation in the group's zone (computed outside sim). */
export const sameDay = (a: number, b: number): boolean => a === b;
